import React, { useEffect, useState } from 'react';
import { apiClient } from '../../api/client';

/**
 * Platform operator: cross-tenant Venues view.
 *
 * A 2-pane screen — left list (venue, tenant, # events, status), right detail
 * pane for the selected venue (address, capacity, contact, parking, event
 * list). Spans ALL tenants (GET /platform/venues), so it lives in operator
 * space, never in a customer hub.
 */

interface VenueRow {
  id: string;
  name: string;
  city: string;
  state: string;
  capacity: number;
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  eventCount: number;
  status: string;
}

interface VenueDetail {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  capacity: number;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  parkingInfo: string | null;
  tenant: { id: string; name: string; slug: string } | null;
  status: string;
  events: { id: string; name: string; date: string | null; status: string; maxCapacity: number }[];
}

function statusStyle(status: string): React.CSSProperties {
  if (status === 'In use') return { background: '#dcfce7', color: '#166534' };
  if (status === 'Placeholder') return { background: '#fef3c7', color: '#92400e' };
  return { background: '#e2e8f0', color: '#475569' }; // Unused
}

interface TenantOption {
  id: string;
  name: string;
  slug: string;
}

interface VenueFormData {
  tenantId: string;
  name: string;
  address: string;
  city: string;
  state: string;
  capacity: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  parkingInfo: string;
}

const emptyVenueForm: VenueFormData = {
  tenantId: '',
  name: '',
  address: '',
  city: '',
  state: '',
  capacity: '',
  contactName: '',
  contactPhone: '',
  contactEmail: '',
  parkingInfo: '',
};

export function PlatformVenuesPage() {
  const [venues, setVenues] = useState<VenueRow[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<VenueDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  // Tenant list for the create picker.
  const [tenants, setTenants] = useState<TenantOption[]>([]);

  // Create/edit modal.
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<VenueFormData>(emptyVenueForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  async function reloadVenues(selectId?: string) {
    const d = await apiClient<VenueRow[]>('/platform/venues');
    setVenues(d);
    if (selectId) setSelectedId(selectId);
    else if (d.length > 0 && !selectedId) setSelectedId(d[0].id);
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [d, ts] = await Promise.all([
          apiClient<VenueRow[]>('/platform/venues'),
          apiClient<TenantOption[]>('/platform/tenants'),
        ]);
        if (!cancelled) {
          setVenues(d);
          setTenants(ts);
          if (d.length > 0) setSelectedId(d[0].id);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load venues');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  function openCreate() {
    setEditingId(null);
    setForm({ ...emptyVenueForm, tenantId: tenants[0]?.id ?? '' });
    setFormError('');
    setShowModal(true);
  }

  function openEdit() {
    if (!detail) return;
    setEditingId(detail.id);
    setForm({
      tenantId: detail.tenant?.id ?? '',
      name: detail.name,
      address: detail.address,
      city: detail.city,
      state: detail.state,
      capacity: String(detail.capacity),
      contactName: detail.contactName ?? '',
      contactPhone: detail.contactPhone ?? '',
      contactEmail: detail.contactEmail ?? '',
      parkingInfo: detail.parkingInfo ?? '',
    });
    setFormError('');
    setShowModal(true);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setFormError('');
    const cap = parseInt(form.capacity, 10);
    if (Number.isNaN(cap) || cap < 0) {
      setFormError('Capacity must be a non-negative number.');
      return;
    }
    if (!editingId && !form.tenantId) {
      setFormError('Choose the organization this venue belongs to.');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const updated = await apiClient<VenueDetail>(`/platform/venues/${editingId}`, {
          method: 'PATCH',
          body: {
            name: form.name,
            address: form.address,
            city: form.city,
            state: form.state,
            capacity: cap,
            contactName: form.contactName,
            contactPhone: form.contactPhone,
            contactEmail: form.contactEmail,
            parkingInfo: form.parkingInfo,
          },
        });
        setDetail(updated);
        await reloadVenues(updated.id);
      } else {
        const created = await apiClient<VenueDetail>('/platform/venues', {
          method: 'POST',
          body: {
            tenantId: form.tenantId,
            name: form.name,
            address: form.address,
            city: form.city,
            state: form.state,
            capacity: cap,
            contactName: form.contactName || undefined,
            contactPhone: form.contactPhone || undefined,
            contactEmail: form.contactEmail || undefined,
            parkingInfo: form.parkingInfo || undefined,
          },
        });
        setDetail(created);
        await reloadVenues(created.id);
      }
      setShowModal(false);
    } catch (err: any) {
      setFormError(err?.message || 'Could not save the venue.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!detail) return;
    if (detail.events.length > 0) {
      alert(`Cannot delete "${detail.name}" — it still has ${detail.events.length} event(s). Reassign or remove those first.`);
      return;
    }
    if (!confirm(`Delete venue "${detail.name}"? This can't be undone.`)) return;
    try {
      await apiClient(`/platform/venues/${detail.id}`, { method: 'DELETE' });
      setDetail(null);
      setSelectedId(null);
      await reloadVenues();
    } catch (err: any) {
      alert(err?.message || 'Could not delete the venue.');
    }
  }

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    let cancelled = false;
    setDetailLoading(true);
    (async () => {
      try {
        const d = await apiClient<VenueDetail>(`/platform/venues/${selectedId}`);
        if (!cancelled) setDetail(d);
      } catch {
        if (!cancelled) setDetail(null);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedId]);

  const filtered = venues.filter((v) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      v.name.toLowerCase().includes(q) ||
      v.tenantName.toLowerCase().includes(q) ||
      `${v.city} ${v.state}`.toLowerCase().includes(q)
    );
  });

  return (
    <div>
      <div style={styles.headingRow}>
        <div>
          <h1 style={styles.h1}>Platform Venues</h1>
          <p style={styles.sub}>Every venue across all organizations on the platform.</p>
        </div>
        <div style={styles.headingRight}>
          <input
            style={styles.search}
            placeholder="Search venue, tenant, city…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button style={styles.newBtn} onClick={openCreate}>
            + New venue
          </button>
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {loading ? (
        <div style={styles.muted}>Loading venues…</div>
      ) : (
        <div style={styles.twoPane}>
          {/* Left: list */}
          <div style={styles.listPane}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Venue</th>
                  <th style={styles.th}>Organization</th>
                  <th style={styles.thNum}>Events</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={4} style={styles.emptyCell}>No venues match.</td></tr>
                ) : filtered.map((v) => (
                  <tr
                    key={v.id}
                    onClick={() => setSelectedId(v.id)}
                    style={{ ...styles.row, ...(v.id === selectedId ? styles.rowActive : {}) }}
                  >
                    <td style={styles.td}>
                      <div style={styles.vName}>{v.name}</div>
                      <div style={styles.vMeta}>{v.city}, {v.state} · cap {v.capacity.toLocaleString()}</div>
                    </td>
                    <td style={styles.td}><span style={styles.tenant}>{v.tenantName}</span></td>
                    <td style={styles.tdNum}>{v.eventCount}</td>
                    <td style={styles.td}><span style={{ ...styles.pill, ...statusStyle(v.status) }}>{v.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Right: detail */}
          <div style={styles.detailPane}>
            {detailLoading ? (
              <div style={styles.muted}>Loading…</div>
            ) : !detail ? (
              <div style={styles.detailEmpty}>Select a venue to see its details.</div>
            ) : (
              <>
                <div style={styles.detailHead}>
                  <h2 style={styles.detailName}>{detail.name}</h2>
                  <span style={{ ...styles.pill, ...statusStyle(detail.status) }}>{detail.status}</span>
                </div>
                <div style={styles.ownerRow}>
                  🏢 <strong>{detail.tenant?.name ?? '(no organization)'}</strong>
                  {detail.tenant?.slug && <span style={styles.slug}>/{detail.tenant.slug}</span>}
                </div>
                <div style={styles.detailActions}>
                  <button style={styles.editBtn} onClick={openEdit}>✏️ Edit venue</button>
                  <button
                    style={styles.deleteBtn}
                    onClick={handleDelete}
                    disabled={detail.events.length > 0}
                    title={detail.events.length > 0 ? 'Reassign its events before deleting' : 'Delete venue'}
                  >
                    🗑️ Delete
                  </button>
                </div>

                <div style={styles.infoGrid}>
                  <Info label="Address" value={detail.address} />
                  <Info label="City / State" value={`${detail.city}, ${detail.state}`} />
                  <Info label="Capacity" value={detail.capacity.toLocaleString()} />
                  <Info label="Events" value={String(detail.events.length)} />
                </div>

                {(detail.contactName || detail.contactPhone || detail.contactEmail || detail.parkingInfo) && (
                  <div style={styles.block}>
                    <h3 style={styles.blockTitle}>Contact & Logistics</h3>
                    {detail.contactName && <div style={styles.kv}>👤 {detail.contactName}</div>}
                    {detail.contactPhone && <div style={styles.kv}>📞 {detail.contactPhone}</div>}
                    {detail.contactEmail && <div style={styles.kv}>✉️ {detail.contactEmail}</div>}
                    {detail.parkingInfo && <div style={styles.kv}>🅿️ {detail.parkingInfo}</div>}
                  </div>
                )}

                <div style={styles.block}>
                  <h3 style={styles.blockTitle}>Events at this venue</h3>
                  {detail.events.length === 0 ? (
                    <div style={styles.muted}>No events scheduled here.</div>
                  ) : (
                    <table style={styles.evTable}>
                      <thead>
                        <tr>
                          <th style={styles.evTh}>Event</th>
                          <th style={styles.evTh}>Date</th>
                          <th style={styles.evTh}>Status</th>
                          <th style={styles.evThNum}>Capacity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.events.map((e) => (
                          <tr key={e.id}>
                            <td style={styles.evTd}>{e.name}</td>
                            <td style={styles.evTd}>{e.date ? e.date.split('T')[0] : '—'}</td>
                            <td style={styles.evTd}>{e.status}</td>
                            <td style={styles.evTdNum}>{e.maxCapacity.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Create / edit venue modal */}
      {showModal && (
        <div style={styles.modalScrim} onClick={() => !saving && setShowModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={editingId ? 'Edit venue' : 'New venue'}>
            <div style={styles.modalHead}>
              <h2 style={styles.modalTitle}>{editingId ? 'Edit venue' : 'New venue'}</h2>
              <button style={styles.modalClose} onClick={() => setShowModal(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleSave} style={styles.form}>
              {formError && <div style={styles.formError}>{formError}</div>}

              {!editingId && (
                <>
                  <label style={styles.label}>Organization</label>
                  <select
                    style={styles.input}
                    value={form.tenantId}
                    onChange={(e) => setForm({ ...form, tenantId: e.target.value })}
                    required
                  >
                    <option value="" disabled>Choose organization…</option>
                    {tenants.map((t) => (
                      <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>
                    ))}
                  </select>
                </>
              )}
              {editingId && detail?.tenant && (
                <div style={styles.ownerStatic}>🏢 {detail.tenant.name} <span style={styles.slug}>/{detail.tenant.slug}</span></div>
              )}

              <label style={styles.label}>Venue name</label>
              <input style={styles.input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Grand Ballroom" required />

              <label style={styles.label}>Address</label>
              <input style={styles.input} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="123 Main St" required />

              <div style={styles.formRow}>
                <div style={styles.formCol}>
                  <label style={styles.label}>City</label>
                  <input style={styles.input} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required />
                </div>
                <div style={styles.formCol}>
                  <label style={styles.label}>State</label>
                  <input style={styles.input} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} required />
                </div>
                <div style={styles.formCol}>
                  <label style={styles.label}>Capacity</label>
                  <input style={styles.input} type="number" min={0} value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} required />
                </div>
              </div>

              <label style={styles.label}>Contact name</label>
              <input style={styles.input} value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} placeholder="Optional" />

              <div style={styles.formRow}>
                <div style={styles.formCol}>
                  <label style={styles.label}>Contact phone</label>
                  <input style={styles.input} value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} placeholder="Optional" />
                </div>
                <div style={styles.formCol}>
                  <label style={styles.label}>Contact email</label>
                  <input style={styles.input} type="email" value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} placeholder="Optional" />
                </div>
              </div>

              <label style={styles.label}>Parking info</label>
              <textarea style={{ ...styles.input, minHeight: 60, resize: 'vertical' }} value={form.parkingInfo} onChange={(e) => setForm({ ...form, parkingInfo: e.target.value })} placeholder="Optional" />

              <div style={styles.modalActions}>
                <button type="button" style={styles.cancelBtn} onClick={() => setShowModal(false)} disabled={saving}>Cancel</button>
                <button type="submit" style={styles.saveBtn} disabled={saving}>
                  {saving ? 'Saving…' : editingId ? 'Save changes' : 'Create venue'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div style={styles.info}>
      <div style={styles.infoLabel}>{label}</div>
      <div style={styles.infoValue}>{value}</div>
    </div>
  );
}

const ACCENT = '#4f46e5';
const styles: Record<string, React.CSSProperties> = {
  headingRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '1.25rem', gap: '1rem', flexWrap: 'wrap' },
  h1: { fontSize: '1.6rem', fontWeight: 700, color: '#0f172a', margin: 0 },
  sub: { color: '#64748b', fontSize: '0.9rem', margin: '0.25rem 0 0' },
  search: { padding: '0.5rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.9rem', minWidth: '240px' },
  muted: { color: '#94a3b8', fontSize: '0.9rem', padding: '1rem' },
  error: { color: '#b91c1c', background: '#fef2f2', padding: '0.6rem 0.9rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.85rem' },
  twoPane: { display: 'grid', gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr)', gap: '1rem', alignItems: 'start' },
  listPane: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' as const },
  th: { textAlign: 'left' as const, padding: '0.6rem 0.9rem', fontSize: '0.72rem', textTransform: 'uppercase' as const, letterSpacing: '0.5px', color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  thNum: { textAlign: 'right' as const, padding: '0.6rem 0.9rem', fontSize: '0.72rem', textTransform: 'uppercase' as const, letterSpacing: '0.5px', color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  row: { cursor: 'pointer', borderBottom: '1px solid #f1f5f9', transition: 'background 0.1s' },
  rowActive: { background: '#eef2ff', boxShadow: `inset 3px 0 0 ${ACCENT}` },
  td: { padding: '0.6rem 0.9rem', fontSize: '0.85rem', color: '#334155', verticalAlign: 'top' as const },
  tdNum: { padding: '0.6rem 0.9rem', fontSize: '0.85rem', color: '#334155', textAlign: 'right' as const, fontWeight: 600 },
  emptyCell: { padding: '1.25rem', textAlign: 'center' as const, color: '#94a3b8', fontSize: '0.85rem' },
  vName: { fontWeight: 600, color: '#0f172a' },
  vMeta: { fontSize: '0.75rem', color: '#94a3b8', marginTop: '2px' },
  tenant: { fontSize: '0.82rem', color: '#475569' },
  pill: { display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.4px' },
  detailPane: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '1.25rem', minHeight: '320px', position: 'sticky' as const, top: '1rem' },
  detailEmpty: { color: '#94a3b8', fontSize: '0.9rem', padding: '2rem', textAlign: 'center' as const },
  detailHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' },
  detailName: { fontSize: '1.25rem', fontWeight: 700, color: '#0f172a', margin: 0 },
  ownerRow: { marginTop: '0.4rem', fontSize: '0.9rem', color: '#334155' },
  slug: { color: '#94a3b8', marginLeft: '4px', fontSize: '0.8rem' },
  infoGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', margin: '1rem 0' },
  info: { background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: '8px', padding: '0.6rem 0.75rem' },
  infoLabel: { fontSize: '0.68rem', textTransform: 'uppercase' as const, letterSpacing: '0.4px', color: '#94a3b8', fontWeight: 700 },
  infoValue: { fontSize: '0.9rem', color: '#0f172a', marginTop: '2px', fontWeight: 600 },
  block: { marginTop: '1rem', borderTop: '1px solid #f1f5f9', paddingTop: '1rem' },
  blockTitle: { fontSize: '0.8rem', textTransform: 'uppercase' as const, letterSpacing: '0.5px', color: '#64748b', margin: '0 0 0.6rem' },
  kv: { fontSize: '0.85rem', color: '#334155', marginBottom: '0.3rem' },
  evTable: { width: '100%', borderCollapse: 'collapse' as const },
  evTh: { textAlign: 'left' as const, padding: '0.4rem 0.5rem', fontSize: '0.72rem', color: '#64748b', borderBottom: '1px solid #e2e8f0' },
  evThNum: { textAlign: 'right' as const, padding: '0.4rem 0.5rem', fontSize: '0.72rem', color: '#64748b', borderBottom: '1px solid #e2e8f0' },
  evTd: { padding: '0.4rem 0.5rem', fontSize: '0.82rem', color: '#334155', borderBottom: '1px solid #f1f5f9' },
  evTdNum: { padding: '0.4rem 0.5rem', fontSize: '0.82rem', color: '#334155', borderBottom: '1px solid #f1f5f9', textAlign: 'right' as const },

  // Header actions
  headingRight: { display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' as const },
  newBtn: { background: ACCENT, color: '#fff', border: 'none', borderRadius: '8px', padding: '0.55rem 1rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', whiteSpace: 'nowrap' as const },

  // Detail actions
  detailActions: { display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap' as const },
  editBtn: { background: '#fff', color: ACCENT, border: `1px solid ${ACCENT}`, borderRadius: '8px', padding: '0.4rem 0.9rem', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' },
  deleteBtn: { background: '#fff', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: '8px', padding: '0.4rem 0.9rem', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' },

  // Modal
  modalScrim: { position: 'fixed' as const, inset: 0, background: 'rgba(11,17,32,0.45)', zIndex: 60, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '4vh 1rem', overflowY: 'auto' as const },
  modal: { background: '#fff', borderRadius: '14px', width: 'min(560px, 100%)', boxShadow: '0 20px 60px rgba(16,24,40,0.25)' },
  modalHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.25rem', borderBottom: '1px solid #e2e8f0' },
  modalTitle: { fontSize: '1.1rem', fontWeight: 800, color: '#0f172a', margin: 0 },
  modalClose: { background: 'none', border: 'none', fontSize: '1.6rem', lineHeight: 1, color: '#64748b', cursor: 'pointer', padding: 0 },
  form: { padding: '1.25rem' },
  formError: { color: '#b91c1c', background: '#fef2f2', border: '1px solid #fca5a5', padding: '0.6rem 0.9rem', borderRadius: '8px', marginBottom: '0.9rem', fontSize: '0.85rem' },
  label: { display: 'block', fontSize: '0.76rem', fontWeight: 600, color: '#374151', margin: '0.75rem 0 0.25rem' },
  input: { width: '100%', boxSizing: 'border-box' as const, padding: '0.55rem 0.7rem', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.9rem' },
  formRow: { display: 'flex', gap: '0.75rem' },
  formCol: { flex: 1, minWidth: 0 },
  ownerStatic: { fontSize: '0.9rem', color: '#334155', background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: '8px', padding: '0.5rem 0.75rem', marginTop: '0.25rem' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', marginTop: '1.25rem' },
  cancelBtn: { background: '#e2e8f0', color: '#334155', border: 'none', borderRadius: '8px', padding: '0.55rem 1.1rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' },
  saveBtn: { background: ACCENT, color: '#fff', border: 'none', borderRadius: '8px', padding: '0.55rem 1.2rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' },
};
